<template>
  <div class="page-container">
    <NavBar
      custom-title="Detail pemesanan alat"
      type="item-mitra-checkout"
      data-test-navbar
    />
    <div class="content max-width-container">
      <BlTextAv
        variation="body-16"
        weight="bold"
        class="content-container ph-16"
      >
        Pilih sistem pembayaran
      </BlTextAv>
      <div v-if="isLoading" class="content-container p-16">
        <BlShimmerAv
          v-for="i in 2"
          :key="i"
          width="100%"
          height="136px"
          style="border-radius: 12px; display: block"
        />
      </div>
      <div v-else class="content-container p-16">
        <template v-for="scheme in paymentSchemes">
          <RadioCard
            v-if="scheme.active !== false"
            :key="scheme.id"
            name="scheme"
            colored
            :disabled="
              checkPaymentSchemeDisabled(scheme.eligibility_rules_ku_trx)
            "
            :checked="selectedScheme && selectedScheme.id === scheme.id"
            :data-testid="`payment-scheme-${scheme.id}`"
            @change="selectedScheme = scheme"
          >
            <template v-slot:header>
              <div>
                <BlTextAv weight="bold">
                  {{ scheme.title }}
                </BlTextAv>
                <div
                  class="bl-text bl-text--caption-12"
                  v-html="scheme.description"
                />
              </div>
            </template>
            <template v-slot:body>
              <div
                v-if="
                  scheme.daily_fee_amount > 0 || scheme.monthly_fee_amount > 0
                "
                class="grid grid-cols-2"
              >
                <BlFlexContainerMv>
                  <BlIconAv
                    name="ico_bayarditempat_minor"
                    class="icon-wrapper"
                  />
                  <div class="ml-8">
                    <BlTextAv variation="caption-12" color="subdued">
                      Biaya aktivasi
                    </BlTextAv>
                    <BlTextAv weight="bold">
                      <span v-if="scheme.full_payment_amount > 0">
                        {{ money(scheme.full_payment_amount) }}
                      </span>
                      <span v-else-if="scheme.deposit_amount > 0">
                        {{ money(scheme.deposit_amount) }}
                      </span>
                      <span v-else-if="scheme.rent_fee_amount > 0">
                        {{ money(scheme.rent_fee_amount) }}
                      </span>
                      <span v-else>Tidak Ada</span>
                    </BlTextAv>
                  </div>
                </BlFlexContainerMv>
                <BlFlexContainerMv>
                  <BlIconAv name="ico_history_minor" class="icon-wrapper" />
                  <div v-if="scheme.daily_fee_amount > 0" class="ml-8">
                    <BlTextAv variation="caption-12" color="subdued">
                      Biaya harian
                    </BlTextAv>
                    <BlTextAv weight="bold">
                      {{
                        scheme.daily_fee_amount > 0
                          ? money(scheme.daily_fee_amount)
                          : 'Tidak Ada'
                      }}
                    </BlTextAv>
                  </div>
                  <div v-else class="ml-8">
                    <BlTextAv variation="caption-12" color="subdued">
                      Biaya bulanan
                    </BlTextAv>
                    <BlTextAv weight="bold">
                      {{
                        scheme.monthly_fee_amount > 0
                          ? money(scheme.monthly_fee_amount)
                          : 'Tidak Ada'
                      }}
                    </BlTextAv>
                  </div>
                </BlFlexContainerMv>
              </div>
              <div v-else class="grid grid-cols-2">
                <BlFlexContainerMv>
                  <BlIconAv
                    name="ico_bayarditempat_minor"
                    class="icon-wrapper"
                  />
                  <div class="ml-8">
                    <BlTextAv variation="caption-12" color="subdued">
                      Harga beli
                    </BlTextAv>
                  </div>
                </BlFlexContainerMv>
                <BlTextAv weight="bold" class="u-align-right">
                  <span v-if="scheme.full_payment_amount > 0">
                    {{ money(scheme.full_payment_amount) }}
                  </span>
                  <span v-else>Gratis</span>
                </BlTextAv>
              </div>
              <BlTextAv
                v-if="
                  checkPaymentSchemeDisabled(scheme.eligibility_rules_ku_trx)
                "
                class="pt-16 text-error"
              >
                Belum bisa pilih paket ini, target transaksi kirim uang belum
                tercapai.
              </BlTextAv>
            </template>
          </RadioCard>
        </template>
      </div>
      <div class="content-container pv-16">
        <BlFlexContainerMv
          justify-content="space-between"
          align-items="center"
          class="ph-16"
        >
          <BlTextAv variation="body-16" weight="bold">
            Pilih alamat pengiriman
          </BlTextAv>
          <BlButtonAv
            variation="link"
            size="extra-small"
            data-testid="btn-tambah-alamat"
            @click.stop="handleAddAddressClick"
          >
            Tambah alamat
          </BlButtonAv>
        </BlFlexContainerMv>
        <div v-if="isLoading" class="content-container p-16">
          <BlShimmerAv
            width="100%"
            height="136px"
            style="border-radius: 12px; display: block"
          />
        </div>
        <div v-else-if="addresses.length <= 0">
          <BlTextAv class="ph-16 pb-16" color="subdued">
            Belum ada alamat pengiriman
          </BlTextAv>
        </div>
        <div v-else class="swiper ph-16">
          <RadioCard
            v-for="address in addresses"
            :key="address.id"
            name="address"
            :data-testid="`address-${address.id}`"
            :checked="selectedAddress && selectedAddress.id === address.id"
            @change="selectedAddress = address"
          >
            <template v-slot:header>
              <BlFlexContainerMv align-items="center">
                <BlIconAv name="ico_shipping" class="mr-8" />
                <BlTextAv weight="bold">
                  {{ address.title }}
                </BlTextAv>
              </BlFlexContainerMv>
            </template>
            <template v-slot:body>
              <BlTextAv>{{ address.full_address }}</BlTextAv>
            </template>
          </RadioCard>
        </div>
        <BlCalloutMv
          v-if="paymentInfo"
          variation="medium"
          class="mh-16"
          data-test-unavailable-banks
        >
          {{ paymentInfo }}
        </BlCalloutMv>
      </div>
      <template
        v-if="purchaseMethod === 'auto' && selectedScheme && selectedAddress"
      >
        <BlSeparatorAv :height="8" />
        <div class="content max-width-container">
          <div class="content-container pb-20 pt-4 ph-16">
            <BlTextAv variation="body-16" weight="bold">
              Punya kode promo?
            </BlTextAv>
            <BlFieldMv
              :error="voucherStatus === 'error'"
              :message="{
                error:
                  voucherStatus === 'error'
                    ? 'Kode tidak terdaftar. Coba cek lagi, ya.'
                    : '',
                hint:
                  voucherStatus === 'success'
                    ? 'Berhasil pakai kode promo.'
                    : voucherStatus === 'loading'
                    ? 'Mengecek kode promo...'
                    : '',
              }"
              :class="{ 'hint-success': voucherStatus === 'success' }"
              data-test-promo-field
            >
              <BlTextfieldAv
                id="voucher-code-input"
                :value="voucherCodeInput"
                autocomplete="off"
                :disabled="isLoading"
                placeholder="Masukkan kode promo"
                data-test-promo-input
                clearable
                @input="handleVoucherCodeChanged"
              />
            </BlFieldMv>
          </div>
        </div>
        <BlSeparatorAv :height="8" />
        <div class="content max-width-container">
          <div class="content-container pb-20 pt-4 ph-16">
            <BlTextAv variation="body-16" weight="bold">
              Metode pembayaran
            </BlTextAv>

            <BlFlexContainerMv justify-content="space-between">
              <BlTextAv>
                Saldo Mitra
              </BlTextAv>
              <BlTextAv>
                {{ money(balance) }}
              </BlTextAv>
            </BlFlexContainerMv>
            <BlTextAv
              v-if="balance < totalAmount"
              color="error"
              @click="openTopupSaldoPage"
            >
              Klik di sini untuk tambah saldo
            </BlTextAv>
          </div>
        </div>
        <BlSeparatorAv :height="8" />
        <div class="content max-width-container">
          <div class="content-container pb-20 pt-4 ph-16">
            <BlTextAv variation="body-16" weight="bold">
              Rincian transaksi
            </BlTextAv>

            <BlFlexContainerMv
              v-for="item in transactionSummary"
              :key="item.title"
              justify-content="space-between"
            >
              <BlTextAv>{{ item.title }}</BlTextAv>
              <BlTextAv :color="item.color">
                {{ item.amount }}
              </BlTextAv>
            </BlFlexContainerMv>

            <div v-if="showFreeInfo">
              <BlCalloutMv variation="medium">
                <BlTextAv variation="body-14">
                  Biaya GRATIS! Rp1
                  <strong>akan dikembalikan maks. 3 hari kerja</strong> setelah
                  berhasil bayar.
                </BlTextAv>
              </BlCalloutMv>
            </div>
          </div>
        </div>
        <BlSeparatorAv :height="8" />
      </template>
      <div class="max-width-container">
        <BlSeparatorAv :height="8" />
        <div class="footer">
          <div
            v-if="
              purchaseMethod === 'auto' && selectedScheme && selectedAddress
            "
          >
            <BlFlexContainerMv justify-content="space-between">
              <BlTextAv variation="body-16" weight="bold">
                Total bayar
              </BlTextAv>
              <BlTextAv variation="body-16" weight="bold">
                {{ money(totalAmount) }}
              </BlTextAv>
            </BlFlexContainerMv>
            <BlTextAv class="mv-16" style="line-height: 20px">
              Dengan klik bayar, kamu sudah menyetujui semua
              <BlLinkAv
                class="text-link-primary"
                data-testid="link-syarat-ketentuan"
                :href="tncUrl"
                target="_blank"
                @click="handleTncClick"
              >
                Syarat Ketentuan
                <span v-if="mitraItem">{{ mitraItem.name }}</span>
              </BlLinkAv>
            </BlTextAv>
          </div>
          <BlTextAv
            v-else-if="purchaseMethod !== 'auto'"
            variation="caption-12"
            color="secondary"
            class="u-align-center mb-16"
          >
            Dengan klik tombol Pesan, kamu sudah memastikan bahwa sistem
            pembayaran dan alamat pengiriman benar.
          </BlTextAv>
          <BlButtonAv
            fullwidth
            data-testid="btn-submit"
            :disabled="
              !selectedAddress || !selectedScheme || voucherStatus === 'loading'
            "
            :is-loading="isLoading"
            @click="handleSubmitClick"
          >
            {{ purchaseMethod === 'auto' ? 'Bayar' : 'Pesan' }}
          </BlButtonAv>
        </div>
      </div>
    </div>
    <AddressFormModal
      :is-active.sync="showAddressModal"
      :mitra-item-id="mitraItemId"
      @submit="addNewAddress"
    />
    <BlSheetMv
      :active="showSuccessModal"
      data-testid="modal-success-submit-order"
      @close="goToTransactionDetailPage"
    >
      <div slot="content" style="height: 90vh;">
        <BlEmptyStateMv
          type="full"
          :image="imgSuccess"
          title="Berhasil pesan Alat ATM Mitra"
          description="Tim kami akan segera menghubungi kamu lewat WhatsApp paling lambat 2x24 jam."
          main-button-text="Lihat detail transaksi"
          style="height: 100%;"
          @mainButtonClick="goToTransactionDetailPage"
        />
      </div>
    </BlSheetMv>
    <InsufficientBalanceDialog
      :is-active="showInsufficientBalanceDialog"
      :balance="balance"
      :total-amount="totalAmount"
      @dismiss-clicked="dismissInsufficientBalanceDialog"
      @redirect-to-top-up-page="openTopupSaldoPage"
    />
    <PurchaseConfirmationDialog
      :is-active="showPurchaseConfirmationDialog"
      :daily-fee="dailyFee"
      :scheme-name="schemeName"
      :is-loading="isLoading"
      :mitra-item-id="mitraItemId"
      @confirm-clicked="submitOrder"
      @dismiss-clicked="dismissPurchaseConfirmationDialog"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import {
  BlButtonAv,
  BlCalloutMv,
  BlEmptyStateMv,
  BlFieldMv,
  BlFlexContainerMv,
  BlLinkAv,
  BlSeparatorAv,
  BlSheetMv,
  BlShimmerAv,
  BlTextAv,
  BlTextfieldAv,
} from '@bukalapak/bazaar-mweb';
import NavBar from 'shared_features/lakupandai/components/NavBar';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
import exclusiveNeoApi from 'shared_js/api/exclusiveApi/exclusiveNeoApi';
import money from 'shared_js/helpers/stringHelper/money';
import publicGeneralTradeApi from 'shared_js/api/publicApi/publicGeneralTradeApi';
import exclusiveMitraItems from 'shared_js/api/exclusiveApi/exclusiveMitraItemsApi';
import buildBuka20Url from 'shared_js/helpers/urlHelper/buildBuka20Url';
import mitraPaymentDomainBaseUrl from 'shared_js/helpers/urlHelper/mitraPaymentDomainBaseUrl';
import paymentMixin from 'shared_features/item-mitra/mixins/paymentMixin';
import RadioCard from '../components/RadioCard.vue';
import AddressFormModal from '../components/AddressFormModal.vue';
import imgSuccess from '../images/img_medium_mitra_sukses.png';
import exclusiveMitraSendMoneyApi from '../../../shared/javascripts/api/exclusiveApi/exclusiveMitraSendMoneyApi';
import InsufficientBalanceDialog from '../components/InsufficientBalanceDialog.vue';
import PurchaseConfirmationDialog from '../components/PurchaseConfirmationDialog.vue';

const BlIconAv = () =>
  import(
    /* webpackChunkName: "bukalapak/bazaar-visual" */ '@bukalapak/bazaar-visual'
  ).then(module => module.BlIconAv);

const TRACKER_SCREEN_NAME = 'item-mitra-checkout-';

export default {
  name: 'ItemMitraCheckoutPage',
  components: {
    AddressFormModal,
    BlButtonAv,
    BlCalloutMv,
    BlEmptyStateMv,
    BlFieldMv,
    BlFlexContainerMv,
    BlIconAv,
    BlLinkAv,
    BlSeparatorAv,
    BlSheetMv,
    BlShimmerAv,
    BlTextAv,
    BlTextfieldAv,
    InsufficientBalanceDialog,
    NavBar,
    PurchaseConfirmationDialog,
    RadioCard,
  },
  mixins: [paymentMixin],
  data() {
    return {
      imgSuccess,
      isLoading: true,
      paymentSchemes: [],
      paymentInfo: undefined,
      purchaseMethod: undefined,
      balance: 0,
      mitraItem: undefined,
      lastMonthTransaction: undefined,
      selectedScheme: undefined,
      deliveryFees: {},
      deliveryFee: 0,
      addresses: [],
      selectedAddress: undefined,
      voucherCodeInput: '',
      voucherStatus: '',
      voucherAmount: 0,
      showAddressModal: false,
      showSuccessModal: false,
      showInsufficientBalanceDialog: false,
      showPurchaseConfirmationDialog: false,
    };
  },
  computed: {
    mitraItemId() {
      return Number(this.$route.query.mitra_item_id);
    },
    transactionSummary() {
      const amount =
        this.selectedScheme?.full_payment_amount ??
        this.selectedScheme?.deposit_amount ??
        this.selectedScheme?.rent_fee_amount ??
        0;
      const selectedProvince = this.selectedAddress?.province?.toLowerCase();
      const matchingProvince = Object.keys(this.deliveryFees || {}).find(
        province => province.toLowerCase() === selectedProvince,
      );
      const deliveryFee = matchingProvince
        ? this.deliveryFees[matchingProvince]
        : 0;
      return [
        {
          key: 'scheme',
          title: this.selectedScheme?.title ?? 'Paket',
          amount: amount > 0 ? money(amount) : 'Gratis',
          amountNumber: amount,
          color: amount > 0 ? 'primary' : 'success',
        },
        {
          key: 'delivery_fee',
          title: 'Biaya pengiriman',
          amount:
            deliveryFee > 0 || this.selectedScheme?.delivery_fee_amount > 0
              ? money(deliveryFee || this.selectedScheme.delivery_fee_amount)
              : 'Gratis',
          amountNumber:
            deliveryFee ?? this.selectedScheme?.delivery_fee_amount ?? 0,
          color:
            deliveryFee > 0 || this.selectedScheme?.delivery_fee_amount > 0
              ? 'primary'
              : 'success',
        },
        ...(this.voucherAmount
          ? [
              {
                key: 'voucher_amount',
                title: 'Promo',
                amount: `- ${money(this.voucherAmount)}`,
                amountNumber: -this.voucherAmount,
                color: 'success',
              },
            ]
          : []),
      ];
    },
    totalAmount() {
      return this.transactionSummary.reduce(
        (acc, item) => acc + item.amountNumber,
        0,
      );
    },
    hasDailyFee() {
      return this.selectedScheme?.daily_fee_amount > 0;
    },
    dailyFee() {
      return this.selectedScheme?.daily_fee_amount ?? 0;
    },
    schemeName() {
      return this.selectedScheme?.title ?? 'Paket';
    },
    tncUrl() {
      return this.mitraItem?.tnc_url;
    },
    showFreeInfo() {
      const amount =
        this.selectedScheme?.full_payment_amount ??
        this.selectedScheme?.deposit_amount ??
        this.selectedScheme?.rent_fee_amount ??
        0;

      // Show callout for all scheme with amount Rp 1
      return amount === 1;
    },
  },
  async mounted() {
    try {
      this.isLoading = true;
      await Promise.all([
        this.fetchPaymentScheme(),
        this.fetchMitraSendMoneyTransactionSummaries(),
        this.fetchGeneralTradeUser(),
        this.fetchBalance(),
      ]);

      this.mitraItem = await this.retrieveMitraItemById({
        id: this.mitraItemId,
      });
      this.purchaseMethod = this.mitraItem?.purchase_method;
      if (this.paymentSchemes.length <= 1) {
        this.selectedScheme = this.paymentSchemes[0];
      }
      this.visitScreenTrack();
    } catch (error) {
      this.$showSnackbar({
        message: 'Gagal mendapatkan data',
        isError: true,
      });
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions('mitraItems', [
      'updateMitraItemTransaction',
      'retrieveMitraItemById',
      'retrieveUserAgentProfile',
    ]),
    money,
    async fetchPaymentScheme() {
      const key = 'mitra-items/payment-schemes';
      const response = await exclusiveNeoApi.retrieveNeoConfig([key]);
      const mitraItemsConfig = response.data?.find(config => config.id === key);
      this.paymentSchemes =
        mitraItemsConfig?.data?.payment_schemes?.[this.mitraItemId] ?? [];
      this.paymentInfo =
        mitraItemsConfig?.data?.attributes?.[this.mitraItemId]?.payment_info;
      this.deliveryFees =
        mitraItemsConfig?.data?.attributes?.[this.mitraItemId]?.delivery_fees ??
        {};
    },
    async fetchMitraSendMoneyTransactionSummaries() {
      const response = await exclusiveMitraSendMoneyApi.retrieveMitraSendMoneyTransactionSummaries(
        {
          number_of_months: 2,
        },
      );
      this.lastMonthTransaction = response.data?.[1] ?? undefined;
    },
    async fetchGeneralTradeUser() {
      try {
        const response = await publicGeneralTradeApi.retrieveGeneralTradeUser();
        this.addresses = response.data?.address
          ? [
              {
                ...response.data.address,
                title: response.data.address.name,
                postalCode: response.data.address.post_code,
                full_address: this.getFullAddress({
                  ...response.data.address,
                  postalCode: response.data.address.post_code,
                }),
              },
            ]
          : [];
      } catch (e) {
        // ignore address error
      }
    },
    async fetchBalance() {
      const response = await this.retrieveUserAgentProfile();
      this.balance = response?.dompet?.saldo ?? 0;
    },
    addNewAddress(address) {
      this.addresses = [
        {
          id: address.title + uniqueId(),
          ...address,
          full_address: this.getFullAddress(address),
        },
        ...this.addresses,
      ];
      this.selectedAddress = this.addresses[0];
    },
    getFullAddress(address) {
      const rtrw =
        address.rt && address.rw ? `, RT ${address.rt} / RW ${address.rw}` : '';
      let text = `${address.address}${rtrw}, ${address.district}, ${address.area}, ${address.city}, ${address.province}`;
      if (address.postalCode) text += `, ${address.postalCode}`;
      return text;
    },
    async submitOrder() {
      if (!this.selectedAddress || !this.selectedScheme || !this.mitraItemId) {
        return;
      }

      try {
        this.isLoading = true;
        this.showPurchaseConfirmationDialog = false;

        const response = await exclusiveMitraItems.createMitraItemTransaction({
          mitra_item_id: this.mitraItemId,
          schema_type: this.selectedScheme.id,
          address: this.selectedAddress.address,
          province: this.selectedAddress.province,
          city: this.selectedAddress.city,
          district: this.selectedAddress.district,
          postal_code: this.selectedAddress.postalCode,
          rt: this.selectedAddress.rt,
          rw: this.selectedAddress.rw,
          voucher_code: this.voucherCodeInput,
          delivery_fee:
            this.transactionSummary
              .find(item => item.key === 'delivery_fee')
              ?.amountNumber?.toString() ?? '',
        });
        this.updateMitraItemTransaction({
          mitraItemId: this.mitraItemId,
          transaction: response.data,
        });

        if (this.purchaseMethod === 'auto') {
          this.redirectToPaymentAuthorizationPage({
            invoiceGroupNumber: response.data?.invoice_group_number,
            transactionId: response.data?.id,
          });
          return;
        }

        this.showSuccessModal = true;
      } catch (error) {
        this.$showSnackbar({
          message: 'Gagal membuat transaksi',
          isError: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async goToTransactionDetailPage() {
      // wait close modal to reset body overflow
      this.showSuccessModal = false;
      await this.$nextTick();

      this.$router.replace({
        name: 'TransactionDetailPage',
        params: { id: this.mitraItemId },
      });
    },
    async openTopupSaldoPage() {
      window.location.replace(
        buildBuka20Url(`${mitraPaymentDomainBaseUrl}/topup`),
      );
    },
    dismissInsufficientBalanceDialog() {
      this.showInsufficientBalanceDialog = false;
    },
    dismissPurchaseConfirmationDialog() {
      this.showPurchaseConfirmationDialog = false;
    },
    validateBeforeSubmit() {
      if (this.purchaseMethod === 'auto' && this.balance < this.totalAmount) {
        this.showInsufficientBalanceDialog = true;
        return;
      }

      if (this.hasDailyFee) {
        this.showPurchaseConfirmationDialog = true;
        return;
      }

      this.submitOrder();
    },
    checkPaymentSchemeDisabled(minTransactionCount) {
      return (
        !this.lastMonthTransaction ||
        this.lastMonthTransaction.count < minTransactionCount
      );
    },
    handleAddAddressClick() {
      this.trackAddAddressClick();
      this.showAddressModal = true;
    },
    trackAddAddressClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: 'click-item-mitra-checkout-tambah-alamat',
          current_screen: `${TRACKER_SCREEN_NAME}${this.mitraItemId}`,
          target_screen: 'address_form_modal',
        });
      }
    },
    handleTncClick() {
      this.trackTncClick();
    },
    trackTncClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: 'click-item-mitra-checkout-tnc',
          current_screen: `${TRACKER_SCREEN_NAME}${this.mitraItemId}`,
          target_screen: this.tncUrl,
        });
      }
    },
    handleVoucherCodeChanged(value) {
      this.voucherCodeInput = value;
      this.voucherStatus = 'default';
      this.voucherAmount = 0;
      if (!this.voucherCodeInput) return;
      this.validateVoucherCode();
    },
    validateVoucherCode: debounce(
      async function validateVoucherCodeDebounced() {
        try {
          this.voucherStatus = 'loading';
          const response = await exclusiveMitraItems.validateVoucherCode({
            voucherCode: this.voucherCodeInput,
            mitraItemId: this.mitraItemId,
            schemaId: this.selectedScheme.id,
          });
          this.voucherStatus = 'success';
          this.voucherAmount = response.data.voucher_amount;
        } catch (error) {
          this.voucherStatus = 'error';
          this.voucherAmount = 0;
        }
      },
      1000,
    ),
    handleSubmitClick() {
      this.trackSubmitClick();
      this.validateBeforeSubmit();
    },
    trackSubmitClick() {
      if (this.$tracker) {
        this.$tracker.itemMitraItemClickTrack({
          click_source: `click-item-mitra-checkout-bayar`,
          current_screen: `${TRACKER_SCREEN_NAME}${this.mitraItemId}`,
          target_screen: 'submit_order',
          params: JSON.stringify({
            mitra_item_id: this.mitraItemId,
            purchase_method: this.purchaseMethod,
          }),
        });
      }
    },
    visitScreenTrack() {
      if (this.$tracker) {
        this.$tracker.itemMitraLandingPageVisitTrack({
          current_screen: `${TRACKER_SCREEN_NAME}${this.mitraItemId}`,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  padding-top: 56px;
}

.content {
  padding-top: 16px;
  margin: auto;
}

.content-container > :not([hidden]) ~ :not([hidden]) {
  margin-top: 16px;
}

.footer {
  padding: 16px;
}

.swiper {
  display: flex;
  padding-top: 4px;
  padding-bottom: 4px;
  justify-content: flex-start;
  gap: 16px;
  overflow-x: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > div {
    flex-grow: 1;
    flex-shrink: 0;
    width: 85%;
  }
}

.icon-wrapper {
  background-color: #f2f2f2;
  border-radius: 4px;
  padding: 2px;
}

.text-link-primary {
  color: #2b4ac7 !important;
}

.bl-field {
  &.hint-success {
    ::v-deep .bl-field__message {
      color: #128c55;
    }
  }
}
</style>
